import moment from 'moment-timezone';
import type ElementBaseInterface from '@this/domain/element_base_interface';
import { camelToSnakeObject } from '@this/src/util';
import type ElementAttachFile from '../element_attach_file';

export type WifiElementJson = {
  id?: number;
  wifiProviderOptions: Record<string, string>;
  startedAt?: string;
  endedAt?: string;
  provider?: string;
  area?: string;
  remark?: string;
  reservationNumber?: string;
  elementAttachFiles?: {
    [key: string]: ElementAttachFile;
  };
  uploadFileData?: {
    [wifiElementId: number]: {
      [travelerId: string]: UploadFileData[];
    };
  };
};

interface UploadFileData {
  id?: number;
  trip_id?: number;
  upload_file_name: string;
  content_type: string;
  upload_file_base64: string;
  traveler_information_id: string | null;
  element_id?: number;
  file_path?: string;
}

/**
 * @deprecated
 * js/src/domain/wifi_element/wifi_element2.ts を使用すること
 */
class WifiElement implements ElementBaseInterface {
  id: number | undefined;

  type: string;

  wifiProviderOptions: Record<string, string>;

  provider: string;

  area: string;

  remark: string;

  reservationNumber: string;

  startedAt: moment.Moment;

  endedAt: moment.Moment;

  uploadFileData?: {
    [wifiElementId: number]: {
      [travelerId: string]: UploadFileData[];
    };
  };

  attachFiles?: ElementAttachFile[];

  constructor(args: WifiElementJson) {
    this.id = args.id;
    this.type = 'wifi';
    this.wifiProviderOptions = camelToSnakeObject(args.wifiProviderOptions || {});
    this.startedAt = moment(args.startedAt);
    this.endedAt = args.endedAt ? moment(args.endedAt) : moment(this.startedAt).add(1, 'hour');
    this.provider = Object.keys(this.wifiProviderOptions)[0] || args.provider || '';
    this.area = args.area || '';
    this.remark = args.remark || '';
    this.reservationNumber = args.reservationNumber || '';
    this.attachFiles = args.elementAttachFiles ? Object.values(args.elementAttachFiles) : [];
    this.uploadFileData = (this.attachFiles ?? []).reduce(
      (acc, file) => {
        const wifiElementId = file.attachableId;
        const travelerId = file.travelerInformationId;
        if (wifiElementId === undefined || travelerId === undefined) return acc;

        const travelerKey = String(travelerId);
        acc[wifiElementId] = acc[wifiElementId] ?? {};
        acc[wifiElementId][travelerKey] = acc[wifiElementId][travelerKey] ?? [];

        acc[wifiElementId][travelerKey].push({
          upload_file_name: file.uploadFileName,
          content_type: file.contentType,
          upload_file_base64: '',
          id: file.id,
          trip_id: file.tripId,
          traveler_information_id: travelerId,
          element_id: file.attachableId,
          file_path: file.filePath
        });

        return acc;
      },
      {} as {
        [wifiElementId: number]: {
          [travelerId: string]: UploadFileData[];
        };
      }
    );
  }

  handleUploadFileData(uploadFileData: UploadFileData, wifiElementId: number, travelerId: string | null) {
    const travelerKey = travelerId ?? 'null';
    if (!this.uploadFileData) {
      this.uploadFileData = {};
    }
    if (!this.uploadFileData[wifiElementId]) {
      this.uploadFileData[wifiElementId] = {};
    }
    if (!this.uploadFileData[wifiElementId][travelerKey]) {
      this.uploadFileData[wifiElementId][travelerKey] = [];
    }
    this.uploadFileData[wifiElementId][travelerKey].push(uploadFileData);

    return app.render();
  }

  handleDeleteFile(wifiElementId: number, travelerId: string | null, index: number) {
    const travelerKey = travelerId ?? 'null';

    if (
      this.uploadFileData?.[wifiElementId]?.[travelerKey]?.length &&
      this.uploadFileData[wifiElementId][travelerKey].length > index
    ) {
      this.uploadFileData[wifiElementId][travelerKey].splice(index, 1);

      if (this.uploadFileData[wifiElementId][travelerKey].length === 0) {
        delete this.uploadFileData[wifiElementId][travelerKey];
      }

      if (Object.keys(this.uploadFileData[wifiElementId]).length === 0) {
        delete this.uploadFileData[wifiElementId];
      }

      return app.render();
      return true;
    }
    return false;
  }

  handleStartedAtChange(value: moment.Moment) {
    this.startedAt = value;
    return app.render();
  }

  handleEndedAtChange(value: moment.Moment) {
    this.endedAt = value;
    return app.render();
  }

  handleChangeProvider(value: string) {
    this.provider = value;
    return app.render();
  }

  handleChangeArea(value: string) {
    this.area = value;
    return app.render();
  }

  handleChangeRemark(value: string) {
    this.remark = value;
    return app.render();
  }

  startDate() {
    return this.startedAt;
  }

  endDate() {
    return this.endedAt;
  }

  description() {
    return `\
<WiFi>
■プロバイダ：${this.providerName()}
■ご利用日時：${this.startedAt.format('MM/DD HH:mm')}〜${this.endedAt.format('MM/DD HH:mm')}
■ご利用エリア：${this.area}
■備考
${this.remark}\
`;
  }

  title() {
    return 'WiFi';
  }

  summary() {
    return `[WiFi]${this.providerName()}`;
  }

  detail(_orderItem: unknown = undefined, _opts: unknown = undefined) {
    return '';
  }

  structuredDetail(_orderItem: unknown = undefined, _opts: unknown = undefined) {
    return [];
  }

  mappingDescription() {
    return this.title();
  }

  providerName() {
    return this.wifiProviderOptions[this.provider] || this.provider || '';
  }

  validationErrors(orderItemStatus = 0) {
    const errors = [];
    const needStatusGuard = orderItemStatus === 7 || orderItemStatus === 13;
    if (this.area === '') {
      errors.push(this.wifiValidationError());
    }
    if (this.endedAt < this.startedAt) {
      errors.push(this.timeValidationError());
    }
    if (this.reservationNumber === '' && needStatusGuard) {
      errors.push(this.reservationNumberValidationError());
    }
    return errors;
  }

  handleReservationNumberChange(value: string) {
    this.reservationNumber = value;
    return app.render();
  }

  updateParams() {
    return {
      id: this.id,
      type: this.type,
      provider: this.provider,
      started_at: this.startedAt.format('YYYY-MM-DD HH:mm:ss'),
      ended_at: this.endedAt.format('YYYY-MM-DD HH:mm:ss'),
      area: this.area,
      remark: this.remark,
      reservation_number: this.reservationNumber,
      element_attach_files:
        (this.attachFiles as ElementAttachFile[])?.map(f => ({
          id: f.id,
          trip_id: f.tripId,
          traveler_information_id: f.travelerInformationId,
          upload_file_name: f.uploadFileName,
          attachable_id: f.attachableId,
          content_type: f.contentType,
          file_path: f.filePath
        })) ?? [],
      ...(this.uploadFileData ? { upload_file_data: this.uploadFileData } : {})
    };
  }

  wifiValidationError() {
    return 'wifiのエリアを入力してください。';
  }

  timeValidationError() {
    return '利用開始以降を指定してください。';
  }

  reservationNumberValidationError() {
    return '注文番号を入力してください。';
  }
}

export default WifiElement;
